// The talent tree (N): a pannable, zoomable map of every node. Click learns a node next to the tree, right-click gives
// one back for copper; hovering explains it. What's learned and what may be comes from api.talents().
import { app } from '../app';
import { api } from '../api';
import { widget, fill, uiScale, fontSize, type UiPointerEvent } from '../ui/compose';
import { hoverTip, hideTip, type TipData } from '../ui/tooltips';
import { button } from './items';
import type { Windows } from './windows';

interface TalentNodeData {
  id: number; name: string; x: number; y: number; tier: number; area: number; color: string; icon: string; aura: boolean; links: number[];
  state: 'taken' | 'open' | 'next' | 'locked'; shared: boolean; tip: TipData;
}
interface TalentData {
  unspent: number; spent: number; points: number; refundCost: number; level: number;
  areas: { name: string; color: string }[]; nodes: TalentNodeData[];
}

/** Node radius in tree pixels, by tier (Start, Small, Notable, Keystone). */
const radius = [10, 6, 9, 13];

export class TalentWindow {
  readonly el = widget('box', { class: 'talent-tree' });
  private readonly canvas = widget('raster', { class: 'sprite' });
  private readonly header = widget('box', { class: 'panel row map-header' });
  private readonly legend = widget('box', { class: 'panel talent-legend' });
  isOpen = false;
  private data: TalentData | null = null;
  private dataKey = '';
  private zoom = 1;
  private cx = 0;
  private cy = 0;
  private hovered: TalentNodeData | null = null;
  private drag: { x: number; y: number; cx: number; cy: number; moved: boolean } | null = null;

  constructor(private readonly w: Windows) {
    this.el.append(this.canvas, this.header, this.legend);
    this.el.style.display = 'none';
    this.canvas.addEventListener('wheel', e => {
      const before = this.toTree(e.clientX, e.clientY);
      this.zoom = Math.max(0.35, Math.min(4, this.zoom * (e.deltaY < 0 ? 1.2 : 1 / 1.2)));
      const after = this.toTree(e.clientX, e.clientY);
      this.cx += before.x - after.x; this.cy += before.y - after.y;
      this.draw();
    });
    this.canvas.addEventListener('mousedown', (e: UiPointerEvent) => {
      e.stopPropagation();
      if (e.button === 0) this.drag = { x: e.clientX, y: e.clientY, cx: this.cx, cy: this.cy, moved: false };
    });
    this.canvas.addEventListener('mousemove', (e: UiPointerEvent) => this.hover(e.clientX, e.clientY));
    this.canvas.addEventListener('mouseleave', () => { if (this.hovered) { this.hovered = null; this.draw(); } });
    this.canvas.addEventListener('contextmenu', (e: UiPointerEvent) => {
      e.preventDefault();
      this.hover(e.clientX, e.clientY);
      if (this.hovered?.state === 'taken' && this.hovered.tier !== 0) this.askRefund(this.hovered);
    });
    hoverTip(this.canvas, () => this.hovered?.tip ?? null);
    window.addEventListener('mousemove', this.move);
    window.addEventListener('mouseup', this.up);
  }

  dispose(): void { window.removeEventListener('mousemove', this.move); window.removeEventListener('mouseup', this.up); }

  private move = (e: MouseEvent) => {
    const d = this.drag;
    if (!d) return;
    if (!d.moved && Math.hypot(e.clientX - d.x, e.clientY - d.y) < 4) return;
    d.moved = true;
    this.cx = d.cx - (e.clientX - d.x) / uiScale / this.zoom;
    this.cy = d.cy - (e.clientY - d.y) / uiScale / this.zoom;
    this.draw();
  };
  private up = () => {
    const d = this.drag;
    this.drag = null;
    if (!d || d.moved || !this.isOpen) return;
    const n = this.hovered;
    if (n?.state === 'open') { api.talentAction(n.id, false); app.audio.play('sfx_ui_open', 0.6, 0, 0, 0.4); }
    else if (n && n.state !== 'taken') api.talentAction(n.id, false); // says why not
  };

  private toTree(x: number, y: number) {
    return { x: (x - innerWidth / 2) / uiScale / this.zoom + this.cx, y: (y - innerHeight / 2) / uiScale / this.zoom + this.cy };
  }

  private hover(x: number, y: number): void {
    const t = this.toTree(x, y);
    let best: TalentNodeData | null = null, bestD = Infinity;
    for (const n of this.data?.nodes ?? []) {
      const d = Math.hypot(n.x - t.x, n.y - t.y), r = radius[n.tier] + 3 / this.zoom;
      if (d <= r && d < bestD) { best = n; bestD = d; }
    }
    if (best !== this.hovered) { this.hovered = best; this.draw(); }
  }

  private askRefund(n: TalentNodeData): void {
    const id = n.id;
    this.w.showConfirm({ title: `Give back ${n.name}?`, color: 'var(--accent)',
      lines: [`It costs ${this.data?.refundCost ?? 0} copper, and the point comes back to spend again.`], yes: 'Give back', no: 'Keep',
      onYes: () => api.talentAction(id, true), stillValid: () => this.isOpen && this.data?.nodes.find(x => x.id === id)?.state === 'taken' });
  }

  toggle(): void { if (this.isOpen) this.close(); else this.open(); }

  open(): void {
    this.isOpen = true;
    this.el.style.display = '';
    // Fit the whole tree (and the area names beyond it) below the header.
    const nodes = (api.talents() as TalentData).nodes ?? [], header = 30, label = fontSize * 1.6;
    const extent = Math.max(100, ...nodes.map(n => Math.max(Math.abs(n.x), Math.abs(n.y)))) + radius[3] + 8;
    this.zoom = Math.max(0.3, Math.min(2, (Math.min(innerWidth / uiScale, innerHeight / uiScale - header) - label * 2) / (extent * 2)));
    this.cx = 0; this.cy = -header / 2 / this.zoom;
    this.dataKey = '';
    this.render();
  }

  close(): void { this.isOpen = false; this.el.style.display = 'none'; this.hovered = null; this.drag = null; hideTip(); }

  /** Rebuilds when what it shows changed (the windows call this on every revision). */
  render(): void {
    if (!this.isOpen) return;
    const d = api.talents() as TalentData;
    const key = JSON.stringify(d);
    if (key === this.dataKey) return;
    this.dataKey = key;
    this.data = d;
    if (this.hovered) this.hovered = d.nodes.find(n => n.id === this.hovered!.id) ?? null;
    const points = d.unspent > 0 ? `${d.unspent} point${d.unspent === 1 ? '' : 's'} to spend` : `No points to spend`;
    // The title and counts centred on the whole header; Close on the right.
    fill(this.header,
      widget('box', { class: 'row', style: { position: 'absolute', left: '0', right: '0', top: '0', bottom: '0', justifyContent: 'center',
        alignItems: 'center', pointerEvents: 'none' } },
        widget('text', { class: 'accent' }, 'Talents'),
        widget('text', { style: { color: d.unspent > 0 ? 'var(--good)' : 'var(--dim)' } }, `   ${points}`),
        widget('text', { class: 'dim' }, `   ${d.spent} / ${d.points} learned`)),
      widget('box', { class: 'grow' }),
      button('Close (N)', () => this.close()));
    fill(this.legend, ...d.areas.map(a => widget('box', { class: 'row' },
      widget('box', { style: { width: '6px', height: '6px', background: a.color } }), widget('text', { class: 'small' }, a.name))));
    this.draw();
  }

  /** A node's icon (its sprite's first frame), dimmed until learned; pixel art stays crisp. */
  private icon(g: CanvasRenderingContext2D, n: TalentNodeData, x: number, y: number, r: number, taken: boolean): void {
    if (!n.icon) return;
    const name = n.icon.replace(/^.*\//, '').replace(/\.png$/, ''), img = app.sprites.images.get(name);
    if (!img) return;
    const size = app.sprites.sizes.get(name) ?? { w: 1, h: 1 }, fw = size.w * 16, fh = size.h * 16;
    const half = r * (n.tier === 3 ? 0.75 : 1.05);
    g.save();
    g.beginPath(); g.arc(x, y, r * 0.95, 0, Math.PI * 2); g.clip();
    g.imageSmoothingEnabled = false;
    g.globalAlpha = taken || n.state === 'open' ? 1 : 0.45;
    g.drawImage(img, 0, 0, fw, fh, x - half, y - half, half * 2, half * 2);
    g.restore();
  }

  draw(): void {
    const d = this.data;
    if (!this.isOpen || !d) return;
    const c = this.canvas, s = uiScale, z = this.zoom * s;
    c.surfaceWidth = innerWidth; c.surfaceHeight = innerHeight;
    c.style.width = `${c.surfaceWidth / uiScale}px`; c.style.height = `${c.surfaceHeight / uiScale}px`;
    const g = c.getContext('2d')!;
    g.fillStyle = '#08060a';
    g.fillRect(0, 0, c.surfaceWidth, c.surfaceHeight);
    const X = (x: number) => (x - this.cx) * z + c.surfaceWidth / 2, Y = (y: number) => (y - this.cy) * z + c.surfaceHeight / 2;
    const byId = new Map(d.nodes.map(n => [n.id, n]));

    // Area names just beyond their keystones, reading away from the centre.
    g.font = `600 ${Math.round(fontSize * s * 1.4)}px 'Fira Sans', sans-serif`;
    for (const k of d.nodes.filter(n => n.tier === 3)) {
      const len = Math.hypot(k.x, k.y) || 1, ux = k.x / len, uy = k.y / len, out = radius[3] + 8;
      g.textAlign = Math.abs(ux) < 0.35 ? 'center' : ux < 0 ? 'right' : 'left';
      g.textBaseline = Math.abs(uy) < 0.35 ? 'middle' : uy < 0 ? 'bottom' : 'top';
      g.fillStyle = k.color; g.globalAlpha = 0.6;
      g.fillText(d.areas.find(a => a.color === k.color)?.name.toUpperCase() ?? '', X(k.x + ux * out), Y(k.y + uy * out));
    }
    g.globalAlpha = 1; g.textAlign = 'center';

    // Links: learned paths glow, the next steps are lit, the rest dim.
    g.lineCap = 'round';
    for (const n of d.nodes) for (const l of n.links) {
      const o = byId.get(l);
      if (!o || o.id < n.id) continue;
      const both = n.state === 'taken' && o.state === 'taken', one = n.state === 'taken' || o.state === 'taken';
      g.strokeStyle = both ? '#f0c060' : one ? '#8c7c62' : '#3e3439';
      g.lineWidth = Math.max(1, (both ? 2.5 : 1.5) * z);
      g.beginPath(); g.moveTo(X(n.x), Y(n.y)); g.lineTo(X(o.x), Y(o.y)); g.stroke();
    }

    // Nodes.
    for (const n of d.nodes) {
      const x = X(n.x), y = Y(n.y), r = radius[n.tier] * z, taken = n.state === 'taken';
      if (n.aura) {
        g.strokeStyle = n.color; g.globalAlpha = taken ? 0.5 : 0.25; g.lineWidth = Math.max(1, z);
        g.beginPath(); g.arc(x, y, r + 4 * z, 0, Math.PI * 2); g.stroke(); g.globalAlpha = 1;
      }
      g.beginPath();
      if (n.tier === 3) { g.moveTo(x, y - r); g.lineTo(x + r, y); g.lineTo(x, y + r); g.lineTo(x - r, y); g.closePath(); }
      else g.arc(x, y, r, 0, Math.PI * 2);
      g.fillStyle = taken ? n.color : n.state === 'open' ? '#2a2226' : '#161216';
      g.fill();
      this.icon(g, n, x, y, r, taken);
      g.lineWidth = Math.max(1, (n.tier >= 2 ? 2.5 : 1.5) * z);
      g.strokeStyle = taken ? '#f8e8c0' : n.state === 'open' ? n.color : n.state === 'next' ? '#7a6e68' : n.color;
      g.globalAlpha = n.state === 'locked' ? 0.35 : 1;
      g.stroke();
      g.globalAlpha = 1;
      if (n.shared && !taken) {
        g.setLineDash([3 * z, 2 * z]); g.strokeStyle = '#96dc5a'; g.lineWidth = Math.max(1, 1.5 * z);
        g.beginPath(); g.arc(x, y, r + 2 * z, 0, Math.PI * 2); g.stroke(); g.setLineDash([]);
      }
      if (n === this.hovered) {
        g.strokeStyle = '#ffffff'; g.lineWidth = Math.max(1, 1.5 * z);
        g.beginPath(); g.arc(x, y, r + 3 * z, 0, Math.PI * 2); g.stroke();
      }
    }
    // Names of the big nodes once there's room for them.
    if (this.zoom >= 0.8) {
      g.font = `500 ${Math.round(fontSize * s * 0.85)}px 'Fira Sans', sans-serif`;
      g.textBaseline = 'top';
      for (const n of d.nodes) if (n.tier >= 2) {
        g.fillStyle = n.state === 'taken' ? '#f0e2c8' : '#9a8c84';
        g.fillText(n.name, X(n.x), Y(n.y) + (radius[n.tier] + 3) * z);
      }
    }
  }
}
